import React, { FC, useEffect, useMemo, useState } from 'react';
import { Input, Segmented, Table, Tooltip } from 'antd';
import { ColumnType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getStockReconciliationShiftItemsCollection } from 'app/store/reducers/stockReconciliation.reducer';
import {
  EStockReconciliationUoM,
  EStockReconciliationVarianceType,
  IStockReconciliationShiftItem,
} from 'entities/StockReconciliation/StockReconciliation.models';
import {
  STOCK_RECONCILIATION_DASH,
  STOCK_RECONCILIATION_DATA_UNAVAILABLE_TOOLTIP,
  STOCK_RECONCILIATION_TRANSFERS_TOOLTIP,
  stockReconciliationVarianceOptions,
} from 'entities/StockReconciliation/StockReconciliation.const';
import { formatStockReconciliationNumber } from 'entities/StockReconciliation/StockReconciliation.helper';

interface IProps {
  shiftKey: string;
  uom: EStockReconciliationUoM;
}

const renderNullableNumber = (value: number | null) => {
  if (value === null || value === undefined) {
    return (
      <Tooltip title={STOCK_RECONCILIATION_DATA_UNAVAILABLE_TOOLTIP}>
        <span>{STOCK_RECONCILIATION_DASH}</span>
      </Tooltip>
    );
  }
  return formatStockReconciliationNumber(value);
};

const renderVariance = (value: number | null) => {
  if (value === null || value === undefined) {
    return (
      <Tooltip title={STOCK_RECONCILIATION_DATA_UNAVAILABLE_TOOLTIP}>
        <span>{STOCK_RECONCILIATION_DASH}</span>
      </Tooltip>
    );
  }
  const formatted = formatStockReconciliationNumber(value);
  return <span style={{ color: value !== 0 ? '#d4380d' : undefined }}>{formatted}</span>;
};

const renderTransfers = (value: number) => (
  <Tooltip title={STOCK_RECONCILIATION_TRANSFERS_TOOLTIP}>
    <span>{formatStockReconciliationNumber(value)}</span>
  </Tooltip>
);

export const StockReconciliationItemsTable: FC<IProps> = ({ shiftKey, uom }) => {
  const dispatch = useAppDispatch();
  const collection = useAppSelector((state) => state.stockReconciliation.shiftItemsByShiftKey[shiftKey]);
  const loading = useAppSelector((state) => !!state.stockReconciliation.isLoadingByShiftKey[shiftKey]);

  const [varianceType, setVarianceType] = useState<EStockReconciliationVarianceType>(EStockReconciliationVarianceType.All);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    dispatch(
      getStockReconciliationShiftItemsCollection({
        shiftKey,
        uom,
        varianceType,
        search: search || undefined,
      })
    );
  }, [shiftKey, uom, varianceType, search, dispatch]);

  const columns = useMemo<ColumnType<IStockReconciliationShiftItem>[]>(
    () => [
      { key: 'category', dataIndex: 'category', title: 'Category', render: (v) => v ?? STOCK_RECONCILIATION_DASH },
      { key: 'item', dataIndex: 'item', title: 'Item' },
      { key: 'code', dataIndex: 'code', title: 'CODE' },
      { key: 'bi', dataIndex: 'bi', title: 'BI', render: renderNullableNumber },
      { key: 'del', dataIndex: 'del', title: 'DEL', render: (v: number) => formatStockReconciliationNumber(v) },
      { key: 'sold', dataIndex: 'sold', title: 'SOLD', render: (v: number) => formatStockReconciliationNumber(v) },
      { key: 'transIn', dataIndex: 'transIn', title: 'TRANS IN', render: renderTransfers },
      { key: 'transOut', dataIndex: 'transOut', title: 'TRANS OUT', render: renderTransfers },
      { key: 'ei', dataIndex: 'ei', title: 'EI', render: renderNullableNumber },
      { key: 'actual', dataIndex: 'actual', title: 'ACTUAL', render: renderNullableNumber },
      { key: 'variance', dataIndex: 'variance', title: 'VAR', render: renderVariance },
    ],
    []
  );

  return (
    <div className="p-3">
      <div className="flex mb-3" style={{ gap: 8 }}>
        <Input.Search
          placeholder="Search item"
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <Segmented
          options={stockReconciliationVarianceOptions}
          value={varianceType}
          onChange={(value) => setVarianceType(value as EStockReconciliationVarianceType)}
        />
      </div>
      <Table<IStockReconciliationShiftItem>
        rowKey="code"
        columns={columns}
        dataSource={collection?.data ?? []}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No items for this shift' }}
      />
    </div>
  );
};
